"use client";

import type { FC } from "react";
import type { AudioSample } from "@mw/shared/payload-types";

import dynamic from "next/dynamic";

import { assertNever } from "@mw/shared/asserts";
import type { DemoConfigDeveloper } from "@/app/_blocks/BlockOmniboxDeveloper";

import type { VideoCardItem } from "@/app/_blocks/BlockBentoGrid/VideoScrollerDemo";

import { resolveSpeechEngineHandoffMode } from "@ui/components/demos/SpeechEngine/speechEngineHandoffMode";
import { filterEntitiesArray, getImageProps, getVideoUrl, getVoiceId } from "@utilities/payload";
import { STTRealtimeDemo } from "../STT/STT";
import { SpeechWrapper } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Speech/SpeechWrapper";
import { SpeechEngineWrapper } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/SpeechEngine/SpeechEngineWrapper";

const SideBySideDemo = dynamic(() => import("./SideBySideDemo"));
const DubbingVideoDemo = dynamic(() => import("./DubbingVideoDemo"));
const VoiceDesignDemo = dynamic(() => import("./VoiceDesignDemo"));
const MusicDemo = dynamic(() => import("./MusicDemo"));
const VideoScrollerDemo = dynamic(() =>
  import("@/app/_blocks/BlockBentoGrid/VideoScrollerDemo").then(mod => ({ default: mod.VideoScrollerDemo })),
);
const AudioTranscriptDemo = dynamic(() =>
  import("@/app/_blocks/BlockContentGrid/AudioTranscriptDemo").then(mod => ({ default: mod.AudioTranscriptDemo })),
);

// Placeholder components for new developer demos
const PlaceholderDemo: FC<{ title: string; description?: string }> = ({ title, description }) => (
  <div className='tw-rounded-2xl tw-bg-white tw-p-8 tw-shadow-sm tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[200px]'>
    <div className='tw-rounded-xl tw-border tw-border-dashed tw-border-gray-300 tw-bg-gray-50 tw-px-6 tw-py-8 tw-text-center'>
      <p className='tw-text-lg tw-font-medium tw-text-gray-900'>{title}</p>
      {description && <p className='tw-mt-2 tw-text-sm tw-text-gray-500'>{description}</p>}
    </div>
  </div>
);

export interface DemoContentDeveloperProps {
  demo: DemoConfigDeveloper;
  speechProps: React.ComponentPropsWithoutRef<typeof SpeechWrapper>;
}

export const DemoContentDeveloper: FC<DemoContentDeveloperProps> = ({ demo, speechProps }) => {
  switch (demo.template) {
    case "text-to-speech":
      return <SpeechWrapper {...speechProps} />;

    case "speech-to-text":
      return <STTRealtimeDemo initiallyVisible={true} size='md' variant='simple' />;

    case "speech-engine":
      return (
        <div className='tw-h-[18rem] sm:tw-h-[22rem] tw-w-full tw-flex tw-items-center tw-justify-center'>
          <SpeechEngineWrapper
            agentId={demo["speech-engine"]?.agentId ?? undefined}
            voiceId={getVoiceId(demo["speech-engine"]?.voice)}
            orbTexture={demo["speech-engine"]?.orbTexture ?? undefined}
            handoffMode={resolveSpeechEngineHandoffMode(demo["speech-engine"]?.handoffMode)}
          />
        </div>
      );

    case "music": {
      const music = demo["music"];
      if (music?.songs && music.songs.length > 0) {
        return (
          <div className='tw-h-[18rem] sm:tw-h-[22rem] tw-w-full tw-flex tw-items-center tw-justify-center'>
            <MusicDemo songs={music.songs} trackingSource='music_demo_developer' />
          </div>
        );
      }
      return <PlaceholderDemo title='Music Generation' description='Generate music with AI' />;
    }

    case "sfx": {
      const sfx = demo["sfx"];
      if (sfx?.items && sfx.items.length > 0) {
        const cards: VideoCardItem[] = sfx.items.map((item, index) => ({
          id: item.id || `sfx-${index}`,
          title: item.title,
          video: getVideoUrl(item.video) || "",
          coverImage: getImageProps(item.coverImage, { fill: true }),
        }));
        return <VideoScrollerDemo cards={cards} showEdgeGradients gradientColor='#F5F3F1' />;
      }
      return <PlaceholderDemo title='Sound Effects' description='Generate sound effects with AI' />;
    }

    case "voice-changer": {
      const voiceChanger = demo["voice-changer"];
      return (
        <div className='tw-h-[17.5rem] sm:tw-h-[21.25rem] tw-w-full tw-flex tw-items-center tw-justify-center'>
          <SideBySideDemo
            leftDisc={voiceChanger?.leftDisc}
            rightDisc={voiceChanger?.rightDisc}
            trackingSource='voice_changer_demo'
          />
        </div>
      );
    }

    case "voice-isolator": {
      const voiceIsolator = demo["voice-isolator"];
      return (
        <div className='tw-h-[17.5rem] sm:tw-h-[21.25rem] tw-w-full tw-flex tw-items-center tw-justify-center'>
          <SideBySideDemo
            leftDisc={voiceIsolator?.leftDisc}
            rightDisc={voiceIsolator?.rightDisc}
            trackingSource='voice_isolator_demo'
          />
        </div>
      );
    }

    case "dubbing": {
      const dubbing = demo["dubbing"];
      return (
        <div className='tw-h-[17.5rem] sm:tw-h-[21.25rem] tw-w-full tw-flex tw-items-center tw-justify-center'>
          <DubbingVideoDemo video={dubbing?.video} dubbingTracks={dubbing?.dubbingTracks} />
        </div>
      );
    }

    case "voice-design": {
      const voiceDesign = demo["voice-design"];
      return (
        <div className='tw-h-[17.5rem] sm:tw-h-[21.25rem] tw-w-full tw-flex tw-items-center tw-justify-center'>
          <VoiceDesignDemo voices={voiceDesign?.voices} />
        </div>
      );
    }

    case "bulk-transcription": {
      const bulkTranscription = demo["bulk-transcription"];
      const items = filterEntitiesArray(bulkTranscription?.items) as AudioSample[] | undefined;
      if (!items?.length) {
        return (
          <PlaceholderDemo title='Bulk Transcription' description='Highlighted transcript synced to audio playback' />
        );
      }
      return (
        <div className='tw-h-[17.5rem] sm:tw-h-[21.25rem] tw-w-full tw-flex tw-items-center tw-justify-center'>
          <AudioTranscriptDemo items={items} playbackVariant={bulkTranscription?.playbackVariant} />
        </div>
      );
    }

    default:
      throw assertNever("DemoContentDeveloper: unknown demoType", demo);
  }
};

export default DemoContentDeveloper;
